import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { currency, formatDate, todayIso, addDaysIso, orderCategory } from "../lib/utils-app";
import {
  Wrench,
  HardHat,
  NotebookPen,
  Package,
  ShoppingCart,
  Award,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Phone,
  MapPin,
  Printer,
  Download,
  MessageCircle,
  Truck,
  Undo2,
  BrainCircuit,
  Sparkles,
  Trash2,
  Landmark,
  Trophy,
  CalendarDays,
  Shirt,
  ShoppingBag,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { downloadBulkChallanPDF, downloadChallanPDF } from "../lib/pdf";
import { downloadBulkReturnMemosAndFlag } from "../lib/return-memo";

// ──────────────────────────────────────────────────────────────────
// QuickAccessGrid — Vendors / Labour / Workbook / Orders gateway
// All four tiles are interactive and live-summarise their domain.
// ──────────────────────────────────────────────────────────────────
export function QuickAccessGrid() {
  // Round 125.9 — Every card must map to a page the current role can
  // actually navigate to. Admin sees everything; manager and
  // supervisor see disjoint subsets that mirror their sidebar nav.
  // Legacy "staff" role (used for admins downgraded via the users
  // page) resolves to manager access, matching AppShell's fallback.
  const [summary, setSummary] = useState(null);
  const [procurement, setProcurement] = useState(null);
  const { user } = useAuth();
  const role = user?.role || "staff";
  const isAdmin = role === "admin" || user?.mobile === "9243425500";
  // Sidebar nav parity — mirrors ROLE_NAV in AppShell so the tiles
  // never dead-link. Kept as a local Set so QuickAccessGrid works
  // without importing AppShell state.
  const NAV = {
    manager: new Set(["dashboard","orders","logistics","calendar","customers","photos","vendors","procurement","users"]),
    supervisor: new Set(["dashboard","orders","logistics","calendar","labour","laundry","cashPayments","inventory","users"]),
    staff: new Set(["dashboard","orders","logistics","calendar","customers","photos","vendors","procurement","users"]),
  };
  const nav = Array.isArray(user?.pages) ? new Set(user.pages) : NAV[role] || NAV.manager;
  const has = (page) => isAdmin || nav.has(page);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        setSummary(data);
      } catch {
        /* silent */
      }
    })();
    if (has("procurement")) {
      (async () => {
        try {
          const { data } = await api.get("/procurement/summary");
          setProcurement(data);
        } catch {
          /* silent */
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, isAdmin]);
  return (
    <section
      className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5"
      data-testid="quick-access-grid"
    >
      {has("vendors") && <VendorsSummaryCard />}
      {has("inventory") && <InventorySummaryCard />}
      {/* Round 125.9 — Weekly calendar tile is universal (everyone
          has /calendar). */}
      <DashboardWeekCard summary={summary} />
      {has("customers") && <DashboardTopCustomersCard summary={summary} />}
      {has("labour") && <DashboardLabourCard summary={summary} />}
      {has("cashPayments") && <DashboardCashPaymentsCard summary={summary} />}
      {has("laundry") && <DashboardLaundryCard summary={summary} />}
      {has("procurement") && <DashboardProcurementCard data={procurement} />}
      {/* Workbook is admin-only; the OrdersTile is universal. */}
      {isAdmin && <DashboardWorkbookCard />}
      <OrdersTile />
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────
// InventorySummaryCard — Round 122
// Native inventory management snapshot on the dashboard (admin +
// supervisor). Shows total items, in-stock, dispatched-out, and the
// top 3 items with lowest availability. Links to /inventory/manage.
// ──────────────────────────────────────────────────────────────────
function InventorySummaryCard() {
  const [data, setData] = useState({ items: [], metrics: null, loading: true });
  useEffect(() => {
    (async () => {
      try {
        const [itemsRes, metricsRes] = await Promise.allSettled([
          api.get("/inv/inventory"),
          api.get("/inv/dashboard/metrics"),
        ]);
        setData({
          items: itemsRes.status === "fulfilled" ? (itemsRes.value.data || []) : [],
          metrics: metricsRes.status === "fulfilled" ? metricsRes.value.data : null,
          loading: false,
        });
      } catch {
        setData({ items: [], metrics: null, loading: false });
      }
    })();
  }, []);

  const totalItems = data.items.length;
  const totalUnits = data.items.reduce((s, it) => s + Number(it.totalQuantity || 0), 0);
  const outUnits = data.items.reduce((s, it) => s + Number(it.outQuantity || 0), 0);
  const availableUnits = Math.max(0, totalUnits - outUnits);
  const lowStock = [...data.items]
    .filter((it) => Number(it.availableQuantity || 0) > 0)
    .sort((a, b) => Number(a.availableQuantity || 0) - Number(b.availableQuantity || 0))
    .slice(0, 3);
  const activeEvents = data.metrics?.active_events ?? data.metrics?.activeEvents ?? 0;

  return (
    <DashTile
      to="/inventory"
      accent="teal"
      icon={Package}
      title="Inventory Management"
      subtitle={`${totalItems} items · ${activeEvents} active dispatch${activeEvents === 1 ? "" : "es"}`}
      openTestId="inventory-open"
    >
      <div data-testid="dashboard-inventory-card" className="contents">
        <div className="flex items-end justify-between gap-4">
          <MetricBlock
            label="Available"
            value={availableUnits.toLocaleString("en-IN")}
            tone="emerald"
            sub={`of ${totalUnits.toLocaleString("en-IN")} units`}
          />
          {outUnits > 0 && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-bold">
                Out on rent
              </div>
              <div className="font-heading text-3xl sm:text-4xl font-extrabold text-amber-700 tabular-nums leading-none mt-1.5">
                {outUnits.toLocaleString("en-IN")}
              </div>
            </div>
          )}
        </div>
        {lowStock.length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-100 space-y-1.5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
              Low stock alert
            </div>
            {lowStock.map((it) => (
              <div key={it.id} className="flex items-center gap-2 text-sm">
                <span
                  className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                    Number(it.availableQuantity || 0) < 10 ? "bg-red-500" : "bg-amber-500"
                  }`}
                />
                <span className="font-semibold text-slate-900 flex-1 truncate">
                  {it.productName}
                </span>
                <span
                  className={`font-heading font-bold tabular-nums ${
                    Number(it.availableQuantity || 0) < 10
                      ? "text-red-700"
                      : "text-amber-700"
                  }`}
                >
                  {it.availableQuantity}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashTile>
  );
}

// ──────────────────────────────────────────────────────────────────
// VendorsSummaryCard — outstanding + work pending + top vendors to pay
// ──────────────────────────────────────────────────────────────────
function VendorsSummaryCard() {
  const [data, setData] = useState({ vendors: [], loading: true });
  useEffect(() => {
    (async () => {
      try {
        const { data: vendors } = await api.get("/vendors");
        setData({ vendors, loading: false });
      } catch {
        setData({ vendors: [], loading: false });
      }
    })();
  }, []);

  const outstanding = data.vendors.reduce((s, v) => s + (v.total_balance || 0), 0);
  const pending = data.vendors.reduce((s, v) => s + (v.pending_count || 0), 0);
  const topDue = [...data.vendors]
    .filter((v) => (v.total_balance || 0) > 0)
    .sort((a, b) => (b.total_balance || 0) - (a.total_balance || 0))
    .slice(0, 3);

  return (
    <DashTile
      to="/vendors"
      accent={outstanding > 0 ? "red" : "emerald"}
      icon={Wrench}
      title="Vendors"
      subtitle={`${data.vendors.length} vendor${data.vendors.length === 1 ? "" : "s"} · ${pending} work pending`}
      openTestId="vendors-open"
    >
      <div data-testid="dashboard-vendors-card" className="contents">
        <div className="flex items-end justify-between gap-4">
          <MetricBlock
            label="Outstanding"
            value={currency(outstanding)}
            tone={outstanding > 0 ? "red" : "emerald"}
            sub={`${pending} pending`}
          />
          {pending > 0 && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-bold">
                Work pending
              </div>
              <div className="font-heading text-3xl sm:text-4xl font-extrabold text-amber-700 tabular-nums leading-none mt-1.5">
                {pending}
              </div>
            </div>
          )}
        </div>
        {topDue.length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-100 space-y-1.5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
              Top vendors to pay
            </div>
            {topDue.map((v) => (
              <div key={v.id} className="flex items-center gap-2 text-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />
                <span className="font-semibold text-slate-900 flex-1 truncate">{v.name}</span>
                <span className="font-heading font-bold text-red-700 tabular-nums">
                  {currency(v.total_balance)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashTile>
  );
}

// ──────────────────────────────────────────────────────────────────
// DashboardLabourCard — Round 97
// Rebuilt to show today + yesterday head counts, monthly payroll,
// paid+advance and pending — sourced from dashboard/summary's new
// labour_snapshot field. (Prev version showed loans outstanding —
// that has moved to the new Drivers & Staff card.)
// ──────────────────────────────────────────────────────────────────
function DashboardLabourCard({ summary }) {
  const l = summary?.labour_snapshot || null;
  const today = l?.today_count ?? null;
  const yesterday = l?.yesterday_count ?? 0;
  const bengali = l?.bengali_today ?? null;
  const assamese = l?.assamese_today ?? null;
  const monthGross = l?.month_gross || 0;
  // Round 97.5 — Paid + Advance is the single number the Labour page
  // shows: `bengali_total_paid + assamese_total_paid` (already summed
  // by the backend into `month_paid_all`).
  const monthPaid = l?.month_paid_all ?? ((l?.month_paid || 0) + (l?.month_advance || 0));
  const monthPending = l?.month_pending || 0;

  return (
    <DashTile
      to="/labour"
      accent={monthPending > 0 ? "amber" : "slate"}
      icon={HardHat}
      title="Labour"
      subtitle={
        l
          ? `${today ?? 0} today · ${yesterday} yesterday`
          : "daily head count & payments"
      }
      openTestId="labour-open"
    >
      <div className="flex items-end justify-between gap-4" data-testid="dashboard-labour-card">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
            Today&apos;s count
          </div>
          <div className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-950 tabular-nums leading-none mt-1.5" data-testid="labour-today-total">
            {today == null ? "—" : today}
          </div>
          {(bengali != null || assamese != null) && (
            <div className="flex items-center gap-1.5 mt-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-testid="labour-today-split">
              <span className="inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                <span className="tabular-nums text-slate-700">{bengali ?? 0}</span>
                <span>Bengali</span>
              </span>
              <span className="text-slate-300">·</span>
              <span className="inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
                <span className="tabular-nums text-slate-700">{assamese ?? 0}</span>
                <span>Assamese</span>
              </span>
            </div>
          )}
          <div className="text-[11px] text-slate-400 mt-0.5">
            {l ? `${yesterday} yesterday` : "no entry yet"}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
            Monthly payroll
          </div>
          <div className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-950 tabular-nums leading-none mt-1.5">
            {currency(monthGross)}
          </div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-bold">Paid + advance</div>
          <div className="font-heading font-bold text-emerald-700 text-base mt-0.5" data-testid="labour-paid-advance">
            {currency(monthPaid)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Pending</div>
          <div
            className={`font-heading font-bold text-base mt-0.5 ${
              monthPending > 0 ? "text-red-700" : "text-emerald-700"
            }`}
            data-testid="labour-pending"
          >
            {currency(monthPending)}
          </div>
        </div>
      </div>
    </DashTile>
  );
}

// ──────────────────────────────────────────────────────────────────
// DashboardLaundryCard — Round 97.3
// Pieces out with each laundry vendor + total amount due. Uses the
// laundry_snapshot bundled in dashboard/summary (no extra API call).
// ──────────────────────────────────────────────────────────────────
function DashboardLaundryCard({ summary }) {
  const l = summary?.laundry_snapshot || null;
  const pieces = l?.total_pieces_out ?? 0;
  const due = l?.total_amount_due ?? 0;
  const vendors = l?.by_vendor || [];
  return (
    <DashTile
      to="/laundry"
      accent={due > 0 ? "amber" : "slate"}
      icon={Shirt}
      title="Laundry"
      subtitle={
        l
          ? `${pieces} pieces out · ${vendors.length} vendor${vendors.length === 1 ? "" : "s"}`
          : "cloth washing tracker"
      }
      openTestId="laundry-open"
    >
      <div className="flex items-end justify-between gap-4" data-testid="dashboard-laundry-card">
        <MetricBlock
          label="Pieces out"
          value={pieces}
          tone="slate"
          sub={vendors.length ? `across ${vendors.length} vendor${vendors.length === 1 ? "" : "s"}` : "nothing pending"}
        />
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-bold">
            Amount due
          </div>
          <div className={`font-heading text-2xl sm:text-3xl font-extrabold tabular-nums leading-none mt-1.5 ${due > 0 ? "text-amber-700" : "text-emerald-700"}`}>
            {currency(due)}
          </div>
        </div>
      </div>
      {vendors.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100 space-y-1.5 text-xs">
          {vendors.slice(0, 4).map((v) => (
            <div key={v.vendor_id} className="flex items-center justify-between gap-2" data-testid="laundry-vendor-row">
              <span className="font-semibold text-slate-800 truncate flex-1 min-w-0">
                {v.vendor_name}
              </span>
              <span className="text-slate-500 tabular-nums shrink-0">
                {v.pieces} pcs
              </span>
              <span className="font-bold text-amber-700 tabular-nums shrink-0 min-w-[70px] text-right">
                {currency(v.amount_due)}
              </span>
            </div>
          ))}
          {vendors.length > 4 && (
            <div className="text-[10px] text-slate-400 italic text-right">
              + {vendors.length - 4} more vendor{vendors.length - 4 === 1 ? "" : "s"}
            </div>
          )}
        </div>
      )}
    </DashTile>
  );
}

// ──────────────────────────────────────────────────────────────────
// DashboardProcurementCard — Round 124.17
// Full-width procurement snapshot on the dashboard (admin-only) —
// mirrors DashboardLaundryCard's layout: hero metric on the left,
// balance due on the right, then a per-supplier balance breakdown.
// ──────────────────────────────────────────────────────────────────
function DashboardProcurementCard({ data }) {
  const openPOs = data?.open_pos ?? 0;
  const openValue = data?.open_value ?? 0;
  const balance = data?.balance_due ?? 0;
  const delayed = data?.delayed_deliveries ?? 0;
  const overdue = data?.overdue_payments ?? 0;
  const suppliers = data?.top_suppliers || [];
  const alert = delayed > 0 || overdue > 0;
  return (
    <DashTile
      to="/procurement"
      accent={alert ? "red" : (balance > 0 ? "amber" : "slate")}
      icon={ShoppingBag}
      title="Procurement"
      subtitle={
        data
          ? `${openPOs} open PO${openPOs === 1 ? "" : "s"} · ${suppliers.length} supplier${suppliers.length === 1 ? "" : "s"}`
          : "purchase orders · suppliers · payments"
      }
      openTestId="procurement-open"
    >
      <div className="flex items-end justify-between gap-4" data-testid="dashboard-procurement-card">
        <MetricBlock
          label="Open POs"
          value={openPOs}
          tone="slate"
          sub={openValue > 0 ? `${currency(openValue)} committed` : "nothing pending"}
        />
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-bold">
            Balance due
          </div>
          <div className={`font-heading text-2xl sm:text-3xl font-extrabold tabular-nums leading-none mt-1.5 ${balance > 0 ? "text-amber-700" : "text-emerald-700"}`}>
            {currency(balance)}
          </div>
          {(delayed > 0 || overdue > 0) && (
            <div className="flex items-center gap-1.5 mt-1.5 text-[10px] font-bold uppercase tracking-wider justify-end">
              {delayed > 0 && (
                <span className="inline-flex items-center gap-1 text-amber-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  <span className="tabular-nums">{delayed}</span>
                  <span>delayed</span>
                </span>
              )}
              {delayed > 0 && overdue > 0 && <span className="text-slate-300">·</span>}
              {overdue > 0 && (
                <span className="inline-flex items-center gap-1 text-red-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                  <span className="tabular-nums">{overdue}</span>
                  <span>overdue</span>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {suppliers.length > 0 ? (
        <div className="mt-4 pt-3 border-t border-slate-100 space-y-1.5 text-xs">
          {suppliers.slice(0, 4).map((s) => (
            <div key={s.supplier_id} className="flex items-center justify-between gap-2" data-testid="procurement-supplier-row">
              <span className="font-semibold text-slate-800 truncate flex-1 min-w-0">
                {s.supplier_name}
              </span>
              <span className="text-slate-500 tabular-nums shrink-0">
                {s.po_count} PO{s.po_count === 1 ? "" : "s"}
              </span>
              <span className={`font-bold tabular-nums shrink-0 min-w-[80px] text-right ${s.balance_due > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                {currency(s.balance_due)}
              </span>
            </div>
          ))}
          {suppliers.length > 4 && (
            <div className="text-[10px] text-slate-400 italic text-right">
              + {suppliers.length - 4} more supplier{suppliers.length - 4 === 1 ? "" : "s"}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-400 italic">
          No open purchase orders — raise one from the Procurement page.
        </div>
      )}
    </DashTile>
  );
}

// ──────────────────────────────────────────────────────────────────
// DashboardCashPaymentsCard — Round 97
// Mirrors the Labour tile shape for drivers & non-labour staff:
// headcount, monthly payroll, paid + pending, loans outstanding.
// ──────────────────────────────────────────────────────────────────
function DashboardCashPaymentsCard({ summary }) {
  const c = summary?.cash_payments_snapshot || null;
  const headcount = c?.headcount ?? 0;
  const monthGross = c?.month_gross || 0;
  // Round 97.5 — "Deductions" replaces "Paid + Advance" per user request.
  // Sums the deduction column on payout rows + this month's loan
  // recovery so ops sees exactly what was subtracted from gross.
  const monthDeductions = (c?.month_deductions || 0) + (c?.month_loan_recovery || 0);
  const monthPending = c?.month_pending || 0;
  const loansOut = c?.loans_outstanding || 0;

  return (
    <DashTile
      to="/cash-payments"
      accent={monthPending > 0 ? "amber" : "slate"}
      icon={Landmark}
      title="Drivers & Support Staff"
      subtitle={
        c ? `${headcount} on roster · cash payouts` : "monthly driver & helper payments"
      }
      openTestId="cash-payments-open"
    >
      <div className="flex items-end justify-between gap-4" data-testid="dashboard-cash-payments-card">
        <MetricBlock
          label="Headcount"
          value={headcount}
          tone="slate"
          sub={loansOut > 0 ? `${currency(loansOut)} loans outstanding` : "all loans clear"}
        />
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
            Monthly payroll
          </div>
          <div className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-950 tabular-nums leading-none mt-1.5">
            {currency(monthGross)}
          </div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-red-700 font-bold">Deductions</div>
          <div className="font-heading font-bold text-red-700 text-base mt-0.5" data-testid="cash-deductions">
            {currency(monthDeductions)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Pending</div>
          <div
            className={`font-heading font-bold text-base mt-0.5 ${
              monthPending > 0 ? "text-amber-700" : "text-emerald-700"
            }`}
            data-testid="cash-pending"
          >
            {currency(monthPending)}
          </div>
        </div>
      </div>
    </DashTile>
  );
}

// ──────────────────────────────────────────────────────────────────
// DashboardWeekCard — Round 97
// Weekly Calendar Summary: total stops this week, the busiest day,
// and a compact per-day bar chart. Clicking opens /calendar.
// ──────────────────────────────────────────────────────────────────
function DashboardWeekCard({ summary }) {
  const week = summary?.week_summary || null;
  const days = summary?.weekly_by_day || [];
  const totalStops = week?.total_stops ?? 0;
  // Round 100 — split the week into deliveries vs returns so ops see
  // "8 out, 5 back" at a glance instead of a single blended number.
  const totalDeliveries = days.reduce((s, d) => s + (d.deliveries || 0), 0);
  const totalReturns = days.reduce((s, d) => s + (d.returns || 0), 0);
  const busiest = week?.busiest_day || null;
  const maxStops = Math.max(1, ...days.map((d) => d.stops || 0));
  // Round 97.5 — Show the date of the busiest day for at-a-glance context
  const busiestDay = busiest?.date ? new Date(busiest.date + "T00:00:00").getDate() : null;

  return (
    <DashTile
      to="/calendar"
      accent={totalStops > 0 ? "slate" : "slate"}
      icon={CalendarDays}
      title="This week"
      subtitle={
        week
          ? `${totalDeliveries} deliveries · ${totalReturns} returns`
          : "day-by-day load"
      }
      openTestId="week-summary-open"
    >
      <div className="flex items-end justify-between gap-4" data-testid="dashboard-week-card">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
            This week
          </div>
          <div className="flex items-baseline gap-3 mt-1.5">
            <div>
              <div className="font-heading text-2xl sm:text-3xl font-extrabold text-red-700 tabular-nums leading-none" data-testid="week-deliveries-count">
                {totalDeliveries}
              </div>
              <div className="text-[10px] uppercase font-bold text-red-700 mt-1">Deliveries</div>
            </div>
            <div className="text-slate-300 text-xl font-thin">/</div>
            <div>
              <div className="font-heading text-2xl sm:text-3xl font-extrabold text-purple-700 tabular-nums leading-none" data-testid="week-returns-count">
                {totalReturns}
              </div>
              <div className="text-[10px] uppercase font-bold text-purple-700 mt-1">Returns</div>
            </div>
          </div>
          <div className="text-[11px] text-slate-400 mt-1.5">
            {busiest?.date && busiest.stops > 0
              ? `Busiest · ${busiest.label} ${busiestDay} (${busiest.stops})`
              : "Nothing scheduled yet"}
          </div>
        </div>
        {busiest?.date && busiest.stops > 0 && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-700 font-bold">
              Busiest day
            </div>
            <div className="font-heading text-2xl sm:text-3xl font-extrabold text-indigo-700 tabular-nums leading-none mt-1.5">
              {busiest.stops}
            </div>
            <div className="text-[10px] text-slate-500 uppercase font-semibold">{busiest.label} {busiestDay}</div>
          </div>
        )}
      </div>
      {/* Per-day bars — Round 100: stacked red (deliveries) + purple (returns).
          Bar height still reflects total stops so the busiest-day cue holds. */}
      <div className="mt-4 pt-3 border-t border-slate-100">
        <div className="flex items-end justify-between gap-1 h-16" data-testid="week-day-bars">
          {days.map((d) => {
            const total = d.stops || 0;
            const h = (total / maxStops) * 100;
            const delH = total > 0 ? ((d.deliveries || 0) / total) * 100 : 0;
            const dNum = d.date ? new Date(d.date + "T00:00:00").getDate() : "";
            const del = d.deliveries || 0;
            const ret = d.returns || 0;
            return (
              <div key={d.date} className="flex flex-col items-center gap-0.5 flex-1 min-w-0">
                {/* Round 100.3 — Number labels above the bar so ops see
                    exact counts at a glance. Format: "8/2" (del/ret)
                    when both present, else the single number. */}
                <div className={`text-[10px] font-extrabold tabular-nums leading-none h-3 ${
                  total > 0 ? (d.is_today ? "text-indigo-700" : "text-slate-700") : "text-transparent"
                }`}
                  data-testid="week-day-total"
                >
                  {total > 0 ? (ret > 0 ? `${del}/${ret}` : `${del}`) : ""}
                </div>
                <div className="w-full flex items-end h-9">
                  <div
                    className="w-full rounded-t-sm overflow-hidden flex flex-col-reverse bg-slate-100"
                    style={{ height: `${h}%`, minHeight: total > 0 ? "3px" : "0px" }}
                    title={`${d.label} ${dNum}: ${del} deliveries · ${ret} returns`}
                  >
                    <div
                      className="w-full bg-red-500"
                      style={{ height: `${delH}%` }}
                    />
                    <div
                      className="w-full bg-purple-500"
                      style={{ height: `${100 - delH}%` }}
                    />
                  </div>
                </div>
                <div className={`text-[10px] font-bold leading-tight text-center ${d.is_today ? "text-indigo-700" : "text-slate-400"}`}>
                  {d.label}
                </div>
                <div className={`text-[9px] font-semibold leading-none ${d.is_today ? "text-indigo-700" : "text-slate-400"}`}>
                  {dNum}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-3 mt-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
            Delivery
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-purple-500" />
            Return
          </span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-500">del/ret per day</span>
        </div>
      </div>
    </DashTile>
  );
}

// ──────────────────────────────────────────────────────────────────
// DashboardTopCustomersCard — Round 97
// Top 5 customers of the CURRENT calendar month by total order value.
// Clicking a row deep-links to the customer ledger (via /customers).
// ──────────────────────────────────────────────────────────────────
function DashboardTopCustomersCard({ summary }) {
  const list = (summary?.top_customers_month || []).slice(0, 5);
  const monthTotal = list.reduce((s, c) => s + Number(c.revenue || 0), 0);
  return (
    <DashTile
      to="/customers"
      accent="slate"
      icon={Trophy}
      title="Top customers"
      subtitle={`This month · ${list.length ? currency(monthTotal) + " from top " + list.length : "no orders yet"}`}
      openTestId="top-customers-open"
    >
      <div data-testid="dashboard-top-customers-card">
        {list.length === 0 ? (
          <div className="text-sm text-slate-500 italic py-6 text-center">
            No orders logged yet this month.
          </div>
        ) : (
          <ol className="space-y-1.5">
            {list.map((c, i) => (
              <li key={`${c.name}-${c.phone}-${i}`} className="flex items-center gap-2 text-sm" data-testid="top-customer-row">
                <span className="h-6 w-6 rounded-full bg-rose-100 text-rose-800 flex items-center justify-center text-[10px] font-bold shrink-0">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-950 truncate">{c.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {c.orders} order{c.orders === 1 ? "" : "s"}
                    {c.outstanding > 0 && (
                      <span className="text-red-700 font-semibold"> · {currency(c.outstanding)} due</span>
                    )}
                  </div>
                </div>
                <span className="font-heading font-bold text-slate-950 tabular-nums shrink-0">
                  {currency(c.revenue)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </DashTile>
  );
}

// ──────────────────────────────────────────────────────────────────
// DashboardWorkbookCard
// ──────────────────────────────────────────────────────────────────
function DashboardWorkbookCard() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/workbook");
        setItems(data || []);
      } catch {
        /* silent */
      }
    })();
  }, []);

  const todayStr = todayIso();
  const in3Str = addDaysIso(todayStr, 3);
  const active = items.filter((i) => i.status !== "done" && i.status !== "archived");
  const dueSoon = active.filter((i) => i.due_date && i.due_date >= todayStr && i.due_date <= in3Str);
  const reminders = active.filter((i) => i.category === "fixed_expense");

  return (
    <DashTile
      to="/workbook"
      accent={dueSoon.length > 0 ? "amber" : "indigo"}
      icon={NotebookPen}
      title="Workbook"
      subtitle="tasks · notes · appointments · reminders"
      openTestId="workbook-open"
    >
      <div className="flex items-end justify-between gap-4">
        <MetricBlock
          label="Active items"
          value={active.length}
          tone="indigo"
          sub={`${reminders.length} reminders`}
        />
        <div className="text-right">
          <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${dueSoon.length > 0 ? "text-amber-700" : "text-slate-500"}`}>
            Due in 3 days
          </div>
          <div className={`font-heading text-2xl sm:text-3xl font-extrabold tabular-nums leading-none mt-1.5 ${dueSoon.length > 0 ? "text-amber-700" : "text-slate-700"}`}>
            {dueSoon.length}
          </div>
        </div>
      </div>
      {dueSoon.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100 space-y-1.5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Next up</div>
          {dueSoon.slice(0, 3).map((i) => (
            <div key={i.id} className="flex items-center gap-2 text-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
              <span className="font-semibold text-slate-900 flex-1 truncate">{i.title}</span>
              <span className="font-heading font-bold text-amber-700 text-xs tabular-nums shrink-0">{formatDate(i.due_date)}</span>
            </div>
          ))}
        </div>
      )}
    </DashTile>
  );
}

// ──────────────────────────────────────────────────────────────────
// OrdersTile — gateway to /orders with status counts that match the
// /orders tab counts (uses the same `orderCategory` derivation).
//   • open      = open_due  + open_received + completed_pending
//   • closed    = orders fully paid AND event passed
//   • pendingPay = orders where event has passed but balance still due
// ──────────────────────────────────────────────────────────────────
function OrdersTile() {
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/orders");
        setOrders(data);
      } catch {
        /* silent */
      }
    })();
  }, []);
  let open = 0, closed = 0, pendingPay = 0;
  for (const o of orders) {
    const cat = orderCategory(o);
    if (cat === "closed") closed += 1;
    else open += 1;
    if (cat === "completed_pending") pendingPay += 1;
  }
  return (
    <DashTile
      to="/orders"
      accent="slate"
      icon={ShoppingCart}
      title="Orders"
      subtitle={`${orders.length} total · ${open} open`}
      openTestId="orders-open"
    >
      <div className="flex items-end justify-between gap-4">
        <MetricBlock label="Open orders" value={open} tone="slate" sub={`${pendingPay} pending payment`} />
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Closed</div>
          <div className="font-heading text-2xl sm:text-3xl font-extrabold text-emerald-700 tabular-nums leading-none mt-1.5">
            {closed}
          </div>
        </div>
      </div>
    </DashTile>
  );
}

// ──────────────────────────────────────────────────────────────────
// DashTile — shared shell for the 4 quick-access tiles above
// ──────────────────────────────────────────────────────────────────
const ACCENT_MAP = {
  red: "bg-red-50 text-red-700 border-red-100",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
  amber: "bg-amber-50 text-amber-700 border-amber-100",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-100",
  teal: "bg-teal-50 text-teal-700 border-teal-100",
  slate: "bg-slate-900 text-white border-slate-900",
};

function DashTile({ to, accent, icon: Icon, title, subtitle, openTestId, children }) {
  return (
    <div className="relative bg-white border border-slate-200 rounded-xl p-4 sm:p-5 hover:border-slate-300 transition-colors">
      <div className="flex items-start gap-3 mb-3 sm:mb-4">
        <div
          className={`h-10 w-10 rounded-lg ${ACCENT_MAP[accent] || ACCENT_MAP.slate} flex items-center justify-center shrink-0`}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-slate-950">{title}</div>
          <div className="text-xs text-slate-500 truncate">{subtitle}</div>
        </div>
        <Link
          to={to}
          data-testid={openTestId}
          className="text-xs font-bold text-red-600 hover:text-red-700 inline-flex items-center gap-0.5 shrink-0"
        >
          Open <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>
      {children}
    </div>
  );
}

function MetricBlock({ label, value, tone = "slate", sub }) {
  const map = {
    red: "text-red-700",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    indigo: "text-indigo-700",
    slate: "text-slate-950",
  };
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${map[tone] || map.slate}`}>
        {label}
      </div>
      <div className={`font-heading text-3xl sm:text-4xl font-extrabold tabular-nums leading-none mt-1.5 ${map[tone] || map.slate}`}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-slate-500 mt-1.5">{sub}</div>}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Tomorrow's schedule cards with Print All
// ──────────────────────────────────────────────────────────────────
export function TomorrowScheduleCards() {
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/orders");
        setOrders(data);
      } catch {
        /* silent */
      }
    })();
  }, []);
  const tomorrow = addDaysIso(todayIso(), 1);
  const dueTomorrow = orders.filter((o) => o.delivery_date === tomorrow);
  const returnsTomorrow = orders.filter((o) => o.return_date === tomorrow);

  return (
    <section
      className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5"
      data-testid="tomorrow-schedule-cards"
    >
      <ScheduleCard
        title="Tomorrow's Delivery Schedule"
        subtitle={`${formatDate(tomorrow)} · ${dueTomorrow.length} ${dueTomorrow.length === 1 ? "order" : "orders"}`}
        orders={dueTomorrow}
        kind="delivery"
        emptyText="No deliveries scheduled tomorrow."
        onPrintAll={() => {
          if (dueTomorrow.length === 0) return toast.error("No deliveries tomorrow");
          downloadBulkChallanPDF(dueTomorrow, { fileLabel: "tomorrow" });
        }}
      />
      <ScheduleCard
        title="Tomorrow's Return Schedule"
        subtitle={`${formatDate(tomorrow)} · ${returnsTomorrow.length} ${returnsTomorrow.length === 1 ? "pickup" : "pickups"}`}
        orders={returnsTomorrow}
        kind="return"
        emptyText="No returns due tomorrow."
        onPrintAll={() => {
          if (returnsTomorrow.length === 0) return toast.error("No returns tomorrow");
          downloadBulkReturnMemosAndFlag(returnsTomorrow, { fileLabel: "tomorrow-returns" });
        }}
      />
    </section>
  );
}

function ScheduleCard({ title, subtitle, orders, kind, emptyText, onPrintAll }) {
  const isDelivery = kind === "delivery";
  const Icon = isDelivery ? Truck : Undo2;
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`schedule-card-${kind}`}>
      <header className="p-4 sm:p-5 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`h-10 w-10 rounded-md ${isDelivery ? "bg-red-50 text-red-700" : "bg-purple-50 text-purple-700"} flex items-center justify-center shrink-0`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-bold text-slate-950">{title}</div>
              <div className="text-xs text-slate-500 mt-0.5 truncate">{subtitle}</div>
            </div>
          </div>
          <Link
            to="/logistics"
            data-testid={`schedule-${kind}-open`}
            className="text-xs font-bold text-red-600 hover:text-red-700 inline-flex items-center gap-0.5 shrink-0"
          >
            Open <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onPrintAll}
            disabled={orders.length === 0}
            data-testid={`schedule-${kind}-print-all`}
            className="border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <Printer className="h-4 w-4 mr-1.5" />
            Print All
          </Button>
        </div>
      </header>
      <div className="divide-y divide-slate-100 max-h-[320px] overflow-y-auto">
        {orders.length === 0 && (
          <div className="p-6 text-sm text-slate-500 text-center">{emptyText}</div>
        )}
        {orders.map((o) => (
          <ScheduleRow key={o.id} order={o} kind={kind} />
        ))}
      </div>
    </div>
  );
}

function ScheduleRow({ order, kind }) {
  const isDelivery = kind === "delivery";
  return (
    <div className="p-3 sm:p-4 flex items-center gap-3" data-testid={`schedule-row-${kind}`}>
      <div className={`h-9 w-9 rounded-md ${isDelivery ? "bg-red-50 text-red-700" : "bg-purple-50 text-purple-700"} flex items-center justify-center font-mono font-bold text-xs shrink-0`}>
        {(order.order_number || "—").slice(-3)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-950 truncate">{order.customer_name}</span>
          {order.payment_status !== "closed" && (
            <span className="text-[10px] uppercase tracking-wider font-bold bg-red-50 text-red-700 px-1.5 py-0.5 rounded">
              Open
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
          <MapPin className="h-3 w-3 shrink-0" />
          <span className="truncate">{order.address || "—"}</span>
          <span className="text-slate-400 shrink-0">·</span>
          <span className="shrink-0">{order.items?.length || 0} items</span>
        </div>
      </div>
      <a
        href={`https://wa.me/${(order.customer_phone || "").replace(/\D/g, "")}`}
        target="_blank"
        rel="noopener noreferrer"
        className="h-8 w-8 rounded-md text-emerald-600 hover:bg-emerald-50 flex items-center justify-center shrink-0"
        title="WhatsApp customer"
      >
        <MessageCircle className="h-4 w-4" />
      </a>
      <button
        type="button"
        onClick={() =>
          isDelivery
            ? downloadChallanPDF(order)
            : import("../lib/return-memo").then((m) => m.downloadReturnMemoAndFlag(order))
        }
        className="h-8 w-8 rounded-md text-slate-500 hover:bg-slate-100 flex items-center justify-center shrink-0"
        title={isDelivery ? "Download Delivery Challan" : "Download Return Memo"}
      >
        <Download className="h-4 w-4" />
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Business Analytics — admin-only, collapsible. Uses /analytics/overview.
// ──────────────────────────────────────────────────────────────────
export function AnalyticsSection() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !data) {
      setLoading(true);
      try {
        const { data: d } = await api.get("/analytics/overview");
        setData(d);
      } catch {
        toast.error("Failed to load analytics");
      } finally {
        setLoading(false);
      }
    }
  };

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.revenue_by_month.map((m) => ({
      month: m.month.slice(5),
      full: m.month,
      total: m.total,
      orders: m.orders,
    }));
  }, [data]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="analytics-section">
      <button
        onClick={toggle}
        data-testid="analytics-toggle-btn"
        className="w-full flex items-center justify-between p-4 sm:p-5 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
            <BarChart3 className="h-4 w-4" />
          </div>
          <div className="text-left min-w-0">
            <div className="font-heading font-bold text-slate-950">Business Analytics</div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">Revenue trend · top items · top customers</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 text-slate-500">
          <span className="text-xs font-semibold uppercase tracking-wider">{open ? "Hide" : "Show"}</span>
          {open ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-200 p-4 sm:p-5 space-y-6" data-testid="analytics-panel">
          {loading ? (
            <div className="py-10 text-center text-slate-500 text-sm">Loading analytics…</div>
          ) : !data ? (
            <div className="py-10 text-center text-slate-500 text-sm">No data yet.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatPill label="Total Orders" value={data.total_orders} />
                <StatPill label="Lifetime Revenue" value={currency(data.total_revenue)} />
                <StatPill label="Active Customers" value={data.top_customers.length} />
                <StatPill label="Tracked Items" value={data.top_items.length} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-3">Revenue by Month</div>
                <div className="h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                      <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => currency(v)} labelFormatter={(l, p) => p?.[0]?.payload?.full || l} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Bar dataKey="total" fill="#dc2626" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-3">
                    <Package className="h-3.5 w-3.5" /> Top Rented Items
                  </div>
                  <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {data.top_items.length === 0 && <div className="p-4 text-sm text-slate-500 text-center">No items yet</div>}
                    {data.top_items.map((it, i) => (
                      <div key={it.name + i} className="flex items-center gap-3 p-3" data-testid="analytics-top-item">
                        <div className="h-7 w-7 rounded-md bg-red-50 text-red-700 flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{it.name}</div>
                          <div className="text-[11px] text-slate-500">{it.orders} order{it.orders === 1 ? "" : "s"}</div>
                        </div>
                        <div className="font-heading font-bold text-slate-950 tabular-nums shrink-0">{it.quantity}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-3">
                    <Award className="h-3.5 w-3.5" /> Top Customers
                  </div>
                  <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {data.top_customers.length === 0 && <div className="p-4 text-sm text-slate-500 text-center">No customers yet</div>}
                    {data.top_customers.map((c, i) => (
                      <div key={c.phone + i} className="flex items-center gap-3 p-3" data-testid="analytics-top-customer">
                        <div className="h-7 w-7 rounded-md bg-slate-900 text-white flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{c.name || c.phone}</div>
                          <div className="text-[11px] text-slate-500">{c.orders} order{c.orders === 1 ? "" : "s"} · {c.phone}</div>
                        </div>
                        <div className="font-heading font-bold text-emerald-700 tabular-nums shrink-0">{currency(c.revenue)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</div>
      <div className="font-heading text-lg sm:text-xl font-extrabold text-slate-950 mt-1 truncate">{value}</div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// ScannerMemoryCard — admin-only collapsible
// ──────────────────────────────────────────────────────────────────
export function ScannerMemoryCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/scan/learning");
      setData(data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const forget = async (original, corrected, field) => {
    if (!window.confirm(`Forget this correction?\n\n"${original}" → "${corrected}"`)) return;
    try {
      await api.delete(
        `/scan/corrections/by-pair?field=${encodeURIComponent(field)}&original=${encodeURIComponent(original)}&corrected=${encodeURIComponent(corrected)}`
      );
      toast.success("Correction removed");
      load();
    } catch {
      toast.error("Could not remove");
    }
  };

  if (loading || !data) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="scanner-memory-card">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="scanner-memory-toggle"
        className="w-full flex items-center justify-between p-4 sm:p-5 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-md bg-gradient-to-br from-red-600 to-rose-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-red-600/30">
            <BrainCircuit className="h-4 w-4" />
          </div>
          <div className="text-left min-w-0">
            <div className="font-heading font-bold text-slate-950 flex items-center gap-2 flex-wrap">
              Scanner Memory
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 text-red-700 text-[10px] font-bold uppercase tracking-wider">
                <Sparkles className="h-2.5 w-2.5" />self-learning
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">
              {data.total_corrections === 0
                ? "Scanner hasn't learnt anything yet."
                : `${data.total_corrections} correction${data.total_corrections === 1 ? "" : "s"} learnt · applied as examples on every new scan`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 text-slate-500">
          <span className="text-xs font-semibold uppercase tracking-wider hidden sm:inline">{open ? "Hide" : "Show"}</span>
          {open ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-200 p-4 sm:p-5 space-y-4" data-testid="scanner-memory-panel">
          {data.accuracy && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-lg p-3">
              <AccuracyTile label="Avg confidence · 7d" value={data.accuracy.avg_confidence_week ? `${Math.round(data.accuracy.avg_confidence_week * 100)}%` : "—"} tone={data.accuracy.avg_confidence_week >= 0.85 ? "emerald" : "amber"} />
              <AccuracyTile label="Scans this week" value={data.accuracy.scans_this_week || 0} />
              <AccuracyTile label="Avg confidence · 30d" value={data.accuracy.avg_confidence_month ? `${Math.round(data.accuracy.avg_confidence_month * 100)}%` : "—"} />
              <AccuracyTile label="Scans this month" value={data.accuracy.scans_this_month || 0} />
              {Array.isArray(data.accuracy.top_corrected_fields) && data.accuracy.top_corrected_fields.length > 0 && (
                <div className="col-span-2 sm:col-span-4 pt-2 border-t border-slate-100">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold mb-1.5">Most-corrected fields (lifetime)</div>
                  <div className="flex flex-wrap gap-1.5">
                    {data.accuracy.top_corrected_fields.map((tf, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white border border-slate-200 rounded-md px-2 py-0.5 text-slate-700">
                        {tf.label}
                        <span className="text-slate-400">·</span>
                        <span className="text-red-700 font-bold">{tf.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <StatPill label="Names learnt" value={data.by_field?.customer_name || 0} />
            <StatPill label="Addresses learnt" value={data.by_field?.address || 0} />
            <StatPill label="Item names learnt" value={data.by_field?.item_name || 0} />
          </div>
          {data.recent.length > 0 ? (
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
              <div className="px-4 py-2 bg-slate-50 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Recent corrections</div>
              {data.recent.map((c, i) => (
                <div key={i} className="px-4 py-3 text-sm flex flex-col sm:flex-row sm:items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-bold shrink-0 sm:w-20">{c.field.replace("_", " ")}</span>
                  <span className="text-slate-400 line-through truncate">{c.original}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                  <span className="text-slate-900 font-semibold truncate">{c.corrected}</span>
                  <button onClick={() => forget(c.original, c.corrected, c.field)} className="ml-auto text-slate-300 hover:text-red-600 shrink-0" title="Forget this correction">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-dashed border-slate-300 rounded-lg py-8 text-center text-sm text-slate-500">
              No corrections yet. Every edit you make to an AI-scanned order teaches the scanner.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AccuracyTile({ label, value, tone = "slate" }) {
  const cls = tone === "emerald" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : "text-slate-950";
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-2.5">
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold">{label}</div>
      <div className={`font-heading text-lg font-extrabold mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}
